import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Save, Loader2, Plus, Trash2, Copy, GripVertical, ChevronDown, ChevronUp,
  Upload, ImageIcon, Video as VideoIcon, Eye, EyeOff, Info, Sparkles, Check, X,
} from "lucide-react";

/**
 * Iter54b — Hero Slides Editor
 * ────────────────────────────────────────────────────────────────────────────
 * Full CMS for the multi-slide homepage hero.
 *   • Carousel behavior config (auto-rotate seconds, transition, loop, hover
 *     pause, tab-inactive pause, arrows, dots, mobile swipe)
 *   • Up to 10 slides — each with image OR MP4 media + per-slide copy + CTAs
 *     + optional featured plan link
 *   • Enable/disable, drag-to-reorder, duplicate, delete
 *
 * Persistence goes through `PUT /api/admin/settings/homepage_hero_slides` in
 * a single save so the CMS stays atomic (no half-saved slide states on the
 * public homepage).
 */

const MAX_SLIDES = 10;
const AUTO_ROTATE_OPTIONS = [
  { value: 0, label: "Off (manual only)" },
  { value: 10, label: "10 seconds" },
  { value: 15, label: "15 seconds (recommended)" },
  { value: 20, label: "20 seconds" },
  { value: 30, label: "30 seconds" },
];
const TRANSITIONS = [
  { value: "kenburns", label: "Ken Burns (recommended · slow zoom)" },
  { value: "fade", label: "Fade" },
  { value: "slide", label: "Slide" },
  { value: "zoom", label: "Zoom" },
];

// Iter54b-ext — media source types. Only the first four render on the
// homepage today; the rest are reserved for the future Interactive
// Experiences section (Matterport / Coohom / 360° tours / AI videos /
// external embeds) and are shown as "Reserved" so admins understand
// their availability.
const MEDIA_TYPES = [
  { value: "image",           label: "Image (upload)",     group: "live" },
  { value: "video_upload",    label: "Video · MP4 upload", group: "live" },
  { value: "video_youtube",   label: "Video · YouTube URL", group: "live" },
  { value: "video_vimeo",     label: "Video · Vimeo URL",   group: "live" },
  { value: "tour_360",        label: "360° Tour (reserved)",       group: "reserved" },
  { value: "tour_matterport", label: "Matterport Tour (reserved)", group: "reserved" },
  { value: "tour_coohom",     label: "Coohom Tour (reserved)",     group: "reserved" },
  { value: "walkthrough",     label: "Walkthrough Video (reserved)", group: "reserved" },
  { value: "ai_video",        label: "AI-Generated Video (reserved)", group: "reserved" },
  { value: "external_embed",  label: "External Embed (reserved)",  group: "reserved" },
];

// Iter54b-ext — CTA destination types. Most map to a plain URL today so
// admin can enter any value; a future admin build can swap the URL field
// for a resource picker (plan search, collection dropdown, etc.).
const CTA_TYPES = [
  { value: "internal",    label: "Internal Page",     placeholder: "/plans" },
  { value: "plan",        label: "House Plan",        placeholder: "plan-slug-or-id" },
  { value: "collection",  label: "Collection",        placeholder: "collection-slug" },
  { value: "category",    label: "Category / Style",  placeholder: "Modern" },
  { value: "blog",        label: "Blog Article",      placeholder: "blog-slug" },
  { value: "external",    label: "External URL",      placeholder: "https://…" },
  { value: "walkthrough", label: "Walkthrough Video", placeholder: "https://… (reserved)" },
  { value: "tour_360",    label: "360° Tour",         placeholder: "https://… (reserved)" },
  { value: "matterport",  label: "Matterport Tour",   placeholder: "https://… (reserved)" },
  { value: "coohom",      label: "Coohom Tour",       placeholder: "https://… (reserved)" },
  { value: "contact",     label: "Contact Form",      placeholder: "/contact" },
];

let _slideSeq = 0;
const nextSlideId = () => `slide-${Date.now().toString(36)}-${(_slideSeq++).toString(36)}`;

const emptySlide = () => ({
  id: nextSlideId(),
  media_type: "image",
  media_url: "",
  media_provider_url: "", // YouTube / Vimeo / external tour URL
  poster_url: "",
  eyebrow: "",
  heading_line1: "New slide headline,",
  heading_italic: "with an accent phrase",
  heading_line3: "and a closing line.",
  description: "",
  overlay_opacity: 0.55,
  primary_cta_type: "internal",
  primary_cta_label: "Browse House Plans",
  primary_cta_href: "/plans",
  secondary_cta_type: "internal",
  secondary_cta_label: "",
  secondary_cta_href: "",
  featured_chip_title: "",
  featured_chip_subtitle: "",
  featured_plan_id: "",
  enabled: true,
  // Iter54b-ext
  publish_at: "",
  expires_at: "",
  seo: { alt_text: "", image_title: "", seo_description: "" },
  analytics: { impressions: 0, clicks: 0, cta_clicks: 0 },
});

const HeroSlidesEditor = ({ onDirtyChange }) => {
  const [carousel, setCarousel] = useState(null);
  const [slides, setSlides] = useState(null);
  const [expanded, setExpanded] = useState({}); // slide id → open?
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customSec, setCustomSec] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/all");
      const hs = data.homepage_hero_slides || {};
      setCarousel(hs.carousel || {
        auto_rotate_seconds: 15,
        infinite_loop: true,
        pause_on_hover: true,
        pause_on_tab_inactive: true,
        show_arrows: true,
        show_dots: true,
        mobile_swipe: true,
        transition: "kenburns",
      });
      // Ensure every slide has an id (older saved data may not).
      const sl = (hs.slides || []).map((s) => ({ ...s, id: s.id || nextSlideId() }));
      setSlides(sl);
    } catch (e) {
      toast.error("Failed to load hero slides", { description: e.response?.data?.detail || "" });
    }
  };
  useEffect(() => { load(); }, []);

  // Iter55d — a slide is publishable only if it carries some media. Images
  // and MP4 uploads require `media_url`; YouTube/Vimeo/reserved-tour types
  // require `media_provider_url`.
  const slideHasMedia = (s) => {
    if (!s) return false;
    const t = s.media_type || "image";
    if (t === "image" || t === "video_upload" || t === "video") return Boolean(s.media_url);
    return Boolean(s.media_provider_url || s.media_url);
  };
  const validationIssues = (slides || []).map((s, i) => {
    if (s.enabled === false) return null;
    if (!slideHasMedia(s)) return `Slide ${i + 1} (${s.heading_line1 || "untitled"}) has no media.`;
    return null;
  }).filter(Boolean);

  const save = async () => {
    if (!carousel || !slides) return;
    if (validationIssues.length > 0) {
      toast.error("Fix media issues first", {
        description: validationIssues.slice(0, 3).join(" · ") + (validationIssues.length > 3 ? ` · +${validationIssues.length - 3} more` : ""),
      });
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings/homepage_hero_slides", { data: { carousel, slides } });
      toast.success("Hero saved", { description: "The homepage carousel will update on next page load." });
      onDirtyChange?.(false);
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const patchSlide = (id, patch) => {
    setSlides((s) => s.map((sl) => (sl.id === id ? { ...sl, ...patch } : sl)));
  };

  const addSlide = () => {
    if (slides.length >= MAX_SLIDES) {
      toast.error("Maximum slides reached", { description: `Only ${MAX_SLIDES} slides allowed.` });
      return;
    }
    const s = emptySlide();
    setSlides((prev) => [...prev, s]);
    setExpanded((e) => ({ ...e, [s.id]: true }));
  };

  const duplicateSlide = (id) => {
    if (slides.length >= MAX_SLIDES) {
      toast.error("Maximum slides reached");
      return;
    }
    const src = slides.find((s) => s.id === id);
    if (!src) return;
    const copy = { ...src, id: nextSlideId(), heading_line1: `${src.heading_line1 || ""} (copy)` };
    const idx = slides.findIndex((s) => s.id === id);
    setSlides((prev) => {
      const next = prev.slice();
      next.splice(idx + 1, 0, copy);
      return next;
    });
    setExpanded((e) => ({ ...e, [copy.id]: true }));
  };

  const deleteSlide = (id) => {
    setSlides((prev) => prev.filter((s) => s.id !== id));
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    setSlides((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIdx(null);
  };

  const uploadMedia = async (id, file) => {
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    // 25 MB cap for videos, 8 MB for images
    const cap = isVideo ? 25 * 1024 * 1024 : 8 * 1024 * 1024;
    if (file.size > cap) {
      toast.error("File too large", { description: `Under ${isVideo ? "25" : "8"} MB please.` });
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const kind = isVideo ? "video" : "image";
      const { data } = await api.post(`/uploads?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      patchSlide(id, {
        media_url: data.url,
        media_type: isVideo ? "video" : "image",
      });
      toast.success("Media uploaded");
    } catch (e) {
      toast.error("Upload failed", { description: e.response?.data?.detail || e.message });
    }
  };

  if (!carousel || !slides) return <div className="text-muted-foreground">Loading hero slides…</div>;

  const autoSecValue = AUTO_ROTATE_OPTIONS.find((o) => o.value === carousel.auto_rotate_seconds)
    ? String(carousel.auto_rotate_seconds)
    : "custom";

  return (
    <div className="max-w-4xl space-y-8" data-testid="hero-slides-editor">
      {/* ── Carousel behavior ─────────────────────────────────────────── */}
      <div className="border border-border p-5 bg-card" data-testid="hero-carousel-config">
        <div className="flex items-start gap-3 mb-5">
          <Info size={16} className="text-accent shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-xl">Carousel Behavior</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Controls how the hero cycles through slides on the public homepage.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="overline text-muted-foreground">Auto-rotate</Label>
            <Select
              value={autoSecValue}
              onValueChange={(v) => {
                if (v === "custom") {
                  setCustomSec(String(carousel.auto_rotate_seconds || ""));
                  setCarousel({ ...carousel, auto_rotate_seconds: carousel.auto_rotate_seconds || 25 });
                } else {
                  setCarousel({ ...carousel, auto_rotate_seconds: Number(v) });
                }
              }}
            >
              <SelectTrigger className="rounded-none h-11 mt-2" data-testid="carousel-auto-rotate">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTO_ROTATE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                ))}
                <SelectItem value="custom">Custom interval…</SelectItem>
              </SelectContent>
            </Select>
            {autoSecValue === "custom" && (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="600"
                  value={carousel.auto_rotate_seconds || ""}
                  onChange={(e) => setCarousel({ ...carousel, auto_rotate_seconds: Math.max(0, Math.min(600, Number(e.target.value) || 0)) })}
                  className="rounded-none h-10 w-32"
                  data-testid="carousel-custom-sec"
                />
                <span className="text-xs text-muted-foreground">seconds per slide</span>
              </div>
            )}
          </div>

          <div>
            <Label className="overline text-muted-foreground">Transition</Label>
            <Select value={carousel.transition || "kenburns"} onValueChange={(v) => setCarousel({ ...carousel, transition: v })}>
              <SelectTrigger className="rounded-none h-11 mt-2" data-testid="carousel-transition">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSITIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
          {[
            ["infinite_loop", "Infinite loop"],
            ["pause_on_hover", "Pause on hover"],
            ["pause_on_tab_inactive", "Pause when tab hidden"],
            ["show_arrows", "Show prev / next arrows"],
            ["show_dots", "Show navigation dots"],
            ["mobile_swipe", "Enable mobile swipe"],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-3 text-sm cursor-pointer" data-testid={`carousel-toggle-${k}`}>
              <Switch
                checked={carousel[k] !== false}
                onCheckedChange={(v) => setCarousel({ ...carousel, [k]: v })}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* ── Slides list ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-xl">Slides <span className="text-muted-foreground text-sm ml-2">{slides.length} / {MAX_SLIDES}</span></h3>
          <p className="text-sm text-muted-foreground mt-0.5">Drag the grip to reorder. Each slide can hold an image or a short video.</p>
        </div>
        <Button
          onClick={addSlide}
          disabled={slides.length >= MAX_SLIDES}
          variant="outline"
          className="rounded-none h-10"
          data-testid="hero-slide-add"
        >
          <Plus size={14} className="mr-2" /> Add slide
        </Button>
      </div>

      {slides.length === 0 && (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          No slides yet. Click "Add slide" to create the first one — the homepage will use the legacy single-hero settings until you do.
        </div>
      )}

      <div className="space-y-3">
        {slides.map((slide, idx) => (
          <SlideRow
            key={slide.id}
            slide={slide}
            index={idx}
            isExpanded={!!expanded[slide.id]}
            hasMedia={slideHasMedia(slide)}
            onToggleExpand={() => setExpanded((e) => ({ ...e, [slide.id]: !e[slide.id] }))}
            onPatch={(patch) => patchSlide(slide.id, patch)}
            onDuplicate={() => duplicateSlide(slide.id)}
            onDelete={() => deleteSlide(slide.id)}
            onDragStart={() => onDragStart(idx)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(idx)}
            uploadMedia={(f) => uploadMedia(slide.id, f)}
            isDragging={dragIdx === idx}
          />
        ))}
      </div>

      {validationIssues.length > 0 && (
        <div className="border border-destructive/60 bg-destructive/10 p-4 text-sm" data-testid="hero-validation-warning">
          <div className="font-medium text-destructive uppercase tracking-[0.14em] text-xs">⚠ Cannot publish — {validationIssues.length} slide{validationIssues.length > 1 ? "s" : ""} missing media</div>
          <ul className="mt-2 text-muted-foreground list-disc list-inside space-y-0.5">
            {validationIssues.map((issue, i) => <li key={i}>{issue}</li>)}
          </ul>
          <p className="text-[11px] italic text-muted-foreground mt-2">Upload media (image · MP4 · YouTube · Vimeo URL) or disable the slide before saving.</p>
        </div>
      )}

      <div className="sticky bottom-4 z-10 flex items-center justify-between border border-border bg-background/95 backdrop-blur px-4 py-3">
        <div className="text-xs text-muted-foreground">
          Save publishes the full carousel to the live homepage.
        </div>
        <Button onClick={save} disabled={saving || validationIssues.length > 0} className="rounded-none bg-primary h-11 px-8" data-testid="hero-slides-save">
          {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Hero Carousel</>}
        </Button>
      </div>
    </div>
  );
};

// ── Iter55e — AI Hero Assistant ─────────────────────────────────────────────
// Given a slide's media (image or poster URL), Claude Sonnet 4.6 generates
// editorial hero copy + layout recommendations. Admin previews the suggestion
// in this panel and applies fields individually or all at once — nothing is
// pushed live until the slide is saved.
const HeroAiAssistant = ({ slide, onPatch }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState({}); // key -> true after admin clicks Apply

  const mediaForAi = slide.poster_url || (slide.media_type === "image" ? slide.media_url : "") || slide.media_url;
  const hasImage = Boolean(mediaForAi);

  const kick = async () => {
    if (!hasImage) {
      toast.error("Upload media first", { description: "The AI Assistant needs a hero image (or a poster image for video slides)." });
      return;
    }
    setBusy(true); setError(""); setResult(null); setProgress(5); setStage("Starting…"); setApplied({});
    try {
      const { data } = await api.post("/ai/hero-generate", {
        image_url: mediaForAi,
        poster_url: slide.poster_url || null,
        current_eyebrow: slide.eyebrow || null,
        current_heading: slide.heading_line1 || null,
        tone: "editorial",
      });
      const jobId = data.job_id;
      // Poll every 2s, max 60 attempts (2 min ceiling)
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: job } = await api.get(`/ai/hero-generate/${jobId}`);
        setProgress(job.progress || 0);
        setStage(job.stage || "");
        if (job.status === "completed") {
          setResult(job.result);
          setBusy(false);
          toast.success("AI hero suggestions ready", { description: "Review below and Apply the fields you like." });
          return;
        }
        if (job.status === "failed") {
          setError(job.error || "AI Hero Assistant failed");
          setBusy(false);
          return;
        }
      }
      setError("Timed out waiting for AI response.");
      setBusy(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "AI request failed");
      setBusy(false);
    }
  };

  const applyField = (key, value) => {
    onPatch({ [key]: value });
    setApplied((a) => ({ ...a, [key]: true }));
  };
  const applySeoField = (subkey, value) => {
    onPatch({ seo: { ...(slide.seo || {}), [subkey]: value } });
    setApplied((a) => ({ ...a, [`seo.${subkey}`]: true }));
  };
  const applyLayout = () => {
    const layout = result?.layout || {};
    const patch = {};
    if (layout.overlay_opacity != null) patch.overlay_opacity = layout.overlay_opacity;
    onPatch(patch);
    setApplied((a) => ({ ...a, layout: true }));
  };

  const applyAll = () => {
    if (!result) return;
    const patch = {};
    const map = ["eyebrow", "heading_line1", "heading_italic", "heading_line3", "description",
                 "primary_cta_label", "primary_cta_href", "secondary_cta_label", "secondary_cta_href"];
    map.forEach((k) => { if (result[k] != null && result[k] !== "") patch[k] = result[k]; });
    if (result.seo) patch.seo = { ...(slide.seo || {}), ...result.seo };
    if (result.layout?.overlay_opacity != null) patch.overlay_opacity = result.layout.overlay_opacity;
    onPatch(patch);
    const nextApplied = {};
    map.forEach((k) => { nextApplied[k] = true; });
    if (result.seo) Object.keys(result.seo).forEach((sk) => { nextApplied[`seo.${sk}`] = true; });
    nextApplied.layout = true;
    setApplied(nextApplied);
    toast.success("All AI fields applied to slide");
  };

  const SuggestionRow = ({ label, value, onApply, applied: isApplied, multiline = false }) => {
    if (value == null || value === "") return null;
    return (
      <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-0">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
          <div className={`mt-1 text-sm ${multiline ? "" : "truncate"}`}>{String(value)}</div>
        </div>
        <Button
          size="sm"
          variant={isApplied ? "outline" : "default"}
          className="rounded-none h-8 px-3 shrink-0"
          onClick={onApply}
          data-testid={`hero-ai-apply-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {isApplied ? <><Check size={12} className="mr-1" />Applied</> : "Apply"}
        </Button>
      </div>
    );
  };

  return (
    <div className="border border-accent/40 bg-accent/5 p-4" data-testid="hero-ai-assistant">
      <div className="flex items-start gap-3">
        <Sparkles size={18} className="text-accent shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display text-base">AI Hero Assistant</div>
              <div className="text-[11px] text-muted-foreground">
                Analyzes this slide's image and drafts editorial copy + layout hints. Nothing is auto-saved — review and apply what you like.
              </div>
            </div>
            <Button
              onClick={kick}
              disabled={busy || !hasImage}
              className="rounded-none h-9 px-4 bg-accent text-accent-foreground"
              data-testid="hero-ai-run"
            >
              {busy
                ? <><Loader2 size={14} className="mr-2 animate-spin" />{stage || "Working…"}</>
                : <><Sparkles size={14} className="mr-2" />{result ? "Regenerate" : "Generate with AI"}</>}
            </Button>
          </div>
          {!hasImage && (
            <div className="text-[11px] text-destructive mt-2" data-testid="hero-ai-no-media">
              Upload media (or add a poster for video slides) before running the assistant.
            </div>
          )}
          {busy && (
            <div className="mt-3 h-1 bg-border overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          {error && (
            <div className="mt-3 text-xs text-destructive border border-destructive/40 bg-destructive/10 p-2" data-testid="hero-ai-error">
              <strong className="mr-1">Error:</strong>{error}
            </div>
          )}
        </div>
      </div>

      {result && (
        <div className="mt-4 border-t border-accent/30 pt-3" data-testid="hero-ai-result">
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-accent text-xs">AI Suggestions</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-none h-8 px-3" onClick={() => setResult(null)} data-testid="hero-ai-dismiss">
                <X size={12} className="mr-1" />Dismiss
              </Button>
              <Button size="sm" className="rounded-none h-8 px-3 bg-primary" onClick={applyAll} data-testid="hero-ai-apply-all">
                <Check size={12} className="mr-1" />Apply all
              </Button>
            </div>
          </div>

          <SuggestionRow label="Eyebrow"           value={result.eyebrow}          applied={applied.eyebrow}          onApply={() => applyField("eyebrow", result.eyebrow)} />
          <SuggestionRow label="Heading line 1"    value={result.heading_line1}    applied={applied.heading_line1}    onApply={() => applyField("heading_line1", result.heading_line1)} />
          <SuggestionRow label="Italic accent"     value={result.heading_italic}   applied={applied.heading_italic}   onApply={() => applyField("heading_italic", result.heading_italic)} />
          <SuggestionRow label="Line 3"            value={result.heading_line3}    applied={applied.heading_line3}    onApply={() => applyField("heading_line3", result.heading_line3)} />
          <SuggestionRow label="Description"       value={result.description}      applied={applied.description}      multiline onApply={() => applyField("description", result.description)} />
          <SuggestionRow label="Primary CTA label" value={result.primary_cta_label} applied={applied.primary_cta_label} onApply={() => applyField("primary_cta_label", result.primary_cta_label)} />
          <SuggestionRow label="Primary CTA href"  value={result.primary_cta_href}  applied={applied.primary_cta_href}  onApply={() => applyField("primary_cta_href", result.primary_cta_href)} />
          <SuggestionRow label="Secondary CTA label" value={result.secondary_cta_label} applied={applied.secondary_cta_label} onApply={() => applyField("secondary_cta_label", result.secondary_cta_label)} />
          <SuggestionRow label="Secondary CTA href"  value={result.secondary_cta_href}  applied={applied.secondary_cta_href}  onApply={() => applyField("secondary_cta_href", result.secondary_cta_href)} />

          {result.seo && (
            <>
              <div className="overline text-accent text-xs mt-4 mb-1">SEO</div>
              <SuggestionRow label="Alt text"          value={result.seo.alt_text}        applied={applied["seo.alt_text"]}        onApply={() => applySeoField("alt_text", result.seo.alt_text)} />
              <SuggestionRow label="Image title"       value={result.seo.image_title}     applied={applied["seo.image_title"]}     onApply={() => applySeoField("image_title", result.seo.image_title)} />
              <SuggestionRow label="SEO description"   value={result.seo.seo_description} applied={applied["seo.seo_description"]} multiline onApply={() => applySeoField("seo_description", result.seo.seo_description)} />
            </>
          )}

          {result.layout && (
            <>
              <div className="overline text-accent text-xs mt-4 mb-1">Layout Recommendation</div>
              <div className="flex items-start justify-between gap-3 py-2">
                <div className="flex-1 min-w-0 text-sm">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Suggested layout</div>
                  <div className="mt-1 space-y-0.5 text-xs">
                    <div>Overlay opacity: <span className="font-mono">{result.layout.overlay_opacity}</span></div>
                    <div>Text position: <span className="font-mono">{result.layout.text_position}</span></div>
                    <div>Text alignment: <span className="font-mono">{result.layout.text_alignment}</span></div>
                    <div>Focal point: <span className="font-mono">x={result.layout.focal_point?.x}%, y={result.layout.focal_point?.y}%</span></div>
                    {result.layout.layout_rationale && (
                      <div className="italic text-muted-foreground mt-1">“{result.layout.layout_rationale}”</div>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={applied.layout ? "outline" : "default"}
                  className="rounded-none h-8 px-3 shrink-0"
                  onClick={applyLayout}
                  data-testid="hero-ai-apply-layout"
                >
                  {applied.layout ? <><Check size={12} className="mr-1" />Applied</> : "Apply overlay"}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">Only <em>overlay opacity</em> maps to the current CMS; the rest are documented for the next hero-layout iteration.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};


// ── One slide row ────────────────────────────────────────────────────────────
const SlideRow = ({
  slide, index, isExpanded, hasMedia, onToggleExpand, onPatch, onDuplicate, onDelete,
  onDragStart, onDragOver, onDrop, uploadMedia, isDragging,
}) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isVideo = slide.media_type === "video";

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    await uploadMedia(f);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`hero-slide-row-${index}`}
      className={`border bg-card ${isDragging ? "opacity-40" : "opacity-100"} ${slide.enabled === false ? "border-dashed border-muted-foreground/40" : "border-border"}`}
    >
      {/* Row header */}
      <div className="flex items-center gap-3 p-3">
        <button
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0 p-1"
          aria-label="Drag to reorder"
          data-testid={`hero-slide-drag-${index}`}
        >
          <GripVertical size={16} />
        </button>
        <div className="w-16 h-10 bg-muted overflow-hidden shrink-0 border border-border flex items-center justify-center">
          {slide.media_url ? (
            isVideo ? (
              <VideoIcon size={16} className="text-muted-foreground" />
            ) : (
              <img src={slide.media_url} alt="" className="w-full h-full object-cover" />
            )
          ) : (
            <ImageIcon size={16} className="text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground uppercase tracking-[0.16em] flex items-center gap-2">
            <span>Slide {index + 1}</span>
            {!hasMedia && (
              <span className="text-destructive normal-case tracking-normal text-[10px] font-medium bg-destructive/10 border border-destructive/40 px-1.5 py-0.5" data-testid={`hero-slide-media-warn-${index}`}>
                ⚠ No media
              </span>
            )}
          </div>
          <div className="font-medium text-sm truncate" data-testid={`hero-slide-title-${index}`}>
            {slide.heading_line1 || <span className="italic text-muted-foreground">Untitled</span>}
          </div>
        </div>
        <button
          onClick={() => {
            // Prevent enabling a slide with no media — that was the bug that
            // caused blank slides on the public homepage.
            if (slide.enabled === false && !hasMedia) return;
            onPatch({ enabled: !(slide.enabled !== false) });
          }}
          disabled={slide.enabled === false && !hasMedia}
          title={slide.enabled === false && !hasMedia ? "Add media before enabling this slide" : ""}
          className="shrink-0 p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label={slide.enabled !== false ? "Disable slide" : "Enable slide"}
          data-testid={`hero-slide-toggle-${index}`}
        >
          {slide.enabled !== false ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
        <button
          onClick={onDuplicate}
          className="shrink-0 p-1.5 text-muted-foreground hover:text-foreground"
          aria-label="Duplicate slide"
          data-testid={`hero-slide-duplicate-${index}`}
        >
          <Copy size={16} />
        </button>
        <button
          onClick={onDelete}
          className="shrink-0 p-1.5 text-muted-foreground hover:text-destructive"
          aria-label="Delete slide"
          data-testid={`hero-slide-delete-${index}`}
        >
          <Trash2 size={16} />
        </button>
        <button
          onClick={onToggleExpand}
          className="shrink-0 p-1.5 text-muted-foreground hover:text-foreground"
          aria-label={isExpanded ? "Collapse" : "Expand"}
          data-testid={`hero-slide-expand-${index}`}
        >
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Expanded editor */}
      {isExpanded && (
        <div className="border-t border-border p-5 space-y-5" data-testid={`hero-slide-body-${index}`}>
          {/* Iter55e — AI Hero Assistant (drafts copy + layout from media) */}
          <HeroAiAssistant slide={slide} onPatch={onPatch} />
          {/* Media */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="overline text-muted-foreground">Media source</Label>
              <Select
                value={slide.media_type || "image"}
                onValueChange={(v) => onPatch({ media_type: v })}
              >
                <SelectTrigger className="rounded-none h-10 mt-2" data-testid={`hero-slide-media-type-${index}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEDIA_TYPES.filter((m) => m.group === "live").map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-t border-border mt-1">
                    Reserved for future homepage sections
                  </div>
                  {MEDIA_TYPES.filter((m) => m.group === "reserved").map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="mt-3">
                <Label className="overline text-muted-foreground text-xs">Preview</Label>
                <div className="mt-1 aspect-video bg-muted border border-border overflow-hidden flex items-center justify-center">
                  {isVideo && slide.media_url ? (
                    <video src={slide.media_url} poster={slide.poster_url || undefined} controls className="w-full h-full object-cover" />
                  ) : slide.media_url ? (
                    <img src={slide.media_url} alt="" className="w-full h-full object-cover" />
                  ) : slide.media_provider_url ? (
                    <div className="text-muted-foreground text-xs px-3 text-center break-all">Embed preview only on public site.<br />URL: {slide.media_provider_url}</div>
                  ) : (
                    <div className="text-muted-foreground text-xs flex flex-col items-center gap-1.5">
                      <ImageIcon size={20} /> No media yet
                    </div>
                  )}
                </div>
              </div>

              {/* Upload path — image or MP4 */}
              {(slide.media_type === "image" || slide.media_type === "video_upload") && (
                <>
                  <input
                    type="file"
                    ref={fileRef}
                    accept={slide.media_type === "image" ? "image/png,image/jpeg,image/webp" : "video/mp4"}
                    className="hidden"
                    onChange={handleFile}
                    data-testid={`hero-slide-file-${index}`}
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="rounded-none h-10 bg-primary"
                      data-testid={`hero-slide-upload-${index}`}
                    >
                      {uploading ? <><Loader2 size={14} className="mr-2 animate-spin" />Uploading…</> : <><Upload size={14} className="mr-2" />{slide.media_type === "image" ? "Upload image" : "Upload MP4"}</>}
                    </Button>
                    {slide.media_url && (
                      <Button
                        variant="outline"
                        onClick={() => onPatch({ media_url: "", poster_url: "" })}
                        className="rounded-none h-10 text-destructive border-destructive"
                        data-testid={`hero-slide-clear-media-${index}`}
                      >
                        Clear media
                      </Button>
                    )}
                  </div>
                </>
              )}

              {/* External video URL path */}
              {(slide.media_type === "video_youtube" || slide.media_type === "video_vimeo") && (
                <div className="mt-3">
                  <Label className="overline text-muted-foreground text-xs">
                    {slide.media_type === "video_youtube" ? "YouTube URL" : "Vimeo URL"}
                  </Label>
                  <Input
                    value={slide.media_provider_url || ""}
                    onChange={(e) => onPatch({ media_provider_url: e.target.value })}
                    placeholder={slide.media_type === "video_youtube" ? "https://youtu.be/… or https://youtube.com/watch?v=…" : "https://vimeo.com/…"}
                    className="rounded-none h-10 mt-1"
                    data-testid={`hero-slide-provider-url-${index}`}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    The public homepage will silently loop this video as the background media.
                  </p>
                </div>
              )}

              {/* Reserved types — schema-only for now */}
              {["tour_360","tour_matterport","tour_coohom","walkthrough","ai_video","external_embed"].includes(slide.media_type) && (
                <div className="mt-3 border border-dashed border-border p-3 text-xs text-muted-foreground bg-muted/30" data-testid={`hero-slide-reserved-${index}`}>
                  <strong className="text-foreground">Reserved for a future Interactive Experiences section.</strong>{" "}
                  Save the URL below now; the CMS + carousel primitive already supports it, and the frontend renderer will light up when the Interactive Experiences section ships.
                  <Input
                    value={slide.media_provider_url || ""}
                    onChange={(e) => onPatch({ media_provider_url: e.target.value })}
                    placeholder="https://…"
                    className="rounded-none h-10 mt-2 bg-background"
                    data-testid={`hero-slide-reserved-url-${index}`}
                  />
                </div>
              )}

              {(isVideo || slide.media_type === "video_youtube" || slide.media_type === "video_vimeo") && (
                <div className="mt-3">
                  <Label className="overline text-muted-foreground text-xs">Poster image URL (optional)</Label>
                  <Input
                    value={slide.poster_url || ""}
                    onChange={(e) => onPatch({ poster_url: e.target.value })}
                    placeholder="https://…"
                    className="rounded-none h-10 mt-1"
                    data-testid={`hero-slide-poster-${index}`}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Shown before the video starts playing.</p>
                </div>
              )}

              <p className="text-[11px] text-muted-foreground mt-3">
                Images: JPG / PNG / WebP · 2400×1400 recommended.<br />
                Videos: MP4 upload · 5–15 seconds · muted autoplay-safe · or link a YouTube / Vimeo URL.
              </p>
            </div>

            {/* Copy */}
            <div className="space-y-3">
              <div>
                <Label className="overline text-muted-foreground text-xs">Eyebrow</Label>
                <Input value={slide.eyebrow || ""} onChange={(e) => onPatch({ eyebrow: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`hero-slide-eyebrow-${index}`} placeholder="Since 2012 · Featured collection" />
              </div>
              <div>
                <Label className="overline text-muted-foreground text-xs">Heading line 1</Label>
                <Input value={slide.heading_line1 || ""} onChange={(e) => onPatch({ heading_line1: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`hero-slide-line1-${index}`} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="overline text-muted-foreground text-xs">Italic accent</Label>
                  <Input value={slide.heading_italic || ""} onChange={(e) => onPatch({ heading_italic: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`hero-slide-italic-${index}`} />
                </div>
                <div>
                  <Label className="overline text-muted-foreground text-xs">Line 3</Label>
                  <Input value={slide.heading_line3 || ""} onChange={(e) => onPatch({ heading_line3: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`hero-slide-line3-${index}`} />
                </div>
              </div>
              <div>
                <Label className="overline text-muted-foreground text-xs">Description</Label>
                <Textarea rows={3} value={slide.description || ""} onChange={(e) => onPatch({ description: e.target.value })} className="rounded-none mt-1" data-testid={`hero-slide-desc-${index}`} />
              </div>
              <div>
                <Label className="overline text-muted-foreground text-xs">Overlay opacity (0-1)</Label>
                <Input type="number" step="0.05" min="0" max="1" value={slide.overlay_opacity ?? 0.55} onChange={(e) => onPatch({ overlay_opacity: Math.max(0, Math.min(1, Number(e.target.value) || 0)) })} className="rounded-none h-10 mt-1" data-testid={`hero-slide-overlay-${index}`} />
              </div>
            </div>
          </div>

          {/* CTAs — now with destination type dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-5">
            {[
              { prefix: "primary",   label: "Primary CTA" },
              { prefix: "secondary", label: "Secondary CTA" },
            ].map(({ prefix, label }) => {
              const typeKey  = `${prefix}_cta_type`;
              const labelKey = `${prefix}_cta_label`;
              const hrefKey  = `${prefix}_cta_href`;
              const currentType = slide[typeKey] || "internal";
              const meta = CTA_TYPES.find((t) => t.value === currentType) || CTA_TYPES[0];
              return (
                <div key={prefix} className="space-y-2">
                  <div className="overline text-accent text-xs">{label}</div>
                  <Input value={slide[labelKey] || ""} onChange={(e) => onPatch({ [labelKey]: e.target.value })} placeholder="Label" className="rounded-none h-10" data-testid={`hero-slide-${prefix}-label-${index}`} />
                  <Select value={currentType} onValueChange={(v) => onPatch({ [typeKey]: v })}>
                    <SelectTrigger className="rounded-none h-10" data-testid={`hero-slide-${prefix}-type-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CTA_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={slide[hrefKey] || ""} onChange={(e) => onPatch({ [hrefKey]: e.target.value })} placeholder={meta.placeholder} className="rounded-none h-10" data-testid={`hero-slide-${prefix}-href-${index}`} />
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-border pt-5">
            <div>
              <Label className="overline text-muted-foreground text-xs">Chip title</Label>
              <Input value={slide.featured_chip_title || ""} onChange={(e) => onPatch({ featured_chip_title: e.target.value })} placeholder="Featured this month" className="rounded-none h-10 mt-1" data-testid={`hero-slide-chip-title-${index}`} />
            </div>
            <div>
              <Label className="overline text-muted-foreground text-xs">Chip subtitle</Label>
              <Input value={slide.featured_chip_subtitle || ""} onChange={(e) => onPatch({ featured_chip_subtitle: e.target.value })} placeholder="e.g. The Solstice Villa" className="rounded-none h-10 mt-1" data-testid={`hero-slide-chip-subtitle-${index}`} />
            </div>
            <div>
              <Label className="overline text-muted-foreground text-xs">Featured Plan ID (optional)</Label>
              <Input value={slide.featured_plan_id || ""} onChange={(e) => onPatch({ featured_plan_id: e.target.value })} placeholder="plan-uuid" className="rounded-none h-10 mt-1" data-testid={`hero-slide-plan-id-${index}`} />
            </div>
          </div>

          {/* Iter54b-ext — Scheduling */}
          <div className="border-t border-border pt-5" data-testid={`hero-slide-schedule-${index}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className="overline text-accent text-xs">Scheduling</div>
              <span className="text-[11px] text-muted-foreground italic">Leave both blank to run permanently.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="overline text-muted-foreground text-xs">Publish at</Label>
                <Input type="datetime-local" value={slide.publish_at || ""} onChange={(e) => onPatch({ publish_at: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`hero-slide-publish-at-${index}`} />
              </div>
              <div>
                <Label className="overline text-muted-foreground text-xs">Expires at</Label>
                <Input type="datetime-local" value={slide.expires_at || ""} onChange={(e) => onPatch({ expires_at: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`hero-slide-expires-at-${index}`} />
              </div>
            </div>
          </div>

          {/* Iter54b-ext — SEO */}
          <div className="border-t border-border pt-5" data-testid={`hero-slide-seo-${index}`}>
            <div className="overline text-accent text-xs mb-3">SEO & Accessibility</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="overline text-muted-foreground text-xs">Alt text (accessibility)</Label>
                <Input
                  value={slide.seo?.alt_text || ""}
                  onChange={(e) => onPatch({ seo: { ...(slide.seo || {}), alt_text: e.target.value } })}
                  className="rounded-none h-10 mt-1"
                  placeholder="Modern villa exterior at sunset"
                  data-testid={`hero-slide-alt-text-${index}`}
                />
              </div>
              <div>
                <Label className="overline text-muted-foreground text-xs">Image title</Label>
                <Input
                  value={slide.seo?.image_title || ""}
                  onChange={(e) => onPatch({ seo: { ...(slide.seo || {}), image_title: e.target.value } })}
                  className="rounded-none h-10 mt-1"
                  placeholder="Featured plan · The Solstice Villa"
                  data-testid={`hero-slide-image-title-${index}`}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="overline text-muted-foreground text-xs">SEO description (screen-reader visible)</Label>
                <Textarea
                  rows={2}
                  value={slide.seo?.seo_description || ""}
                  onChange={(e) => onPatch({ seo: { ...(slide.seo || {}), seo_description: e.target.value } })}
                  className="rounded-none mt-1"
                  placeholder="A short description of this slide's content for search engines & assistive tech."
                  data-testid={`hero-slide-seo-description-${index}`}
                />
              </div>
            </div>
          </div>

          {/* Iter54b-ext — Analytics readout */}
          <div className="border-t border-border pt-5" data-testid={`hero-slide-analytics-${index}`}>
            <div className="overline text-accent text-xs mb-3">Analytics</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-border p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Impressions</div>
                <div className="font-display text-2xl mt-1 tabular-nums" data-testid={`hero-slide-impressions-${index}`}>{slide.analytics?.impressions ?? 0}</div>
              </div>
              <div className="border border-border p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">CTA clicks</div>
                <div className="font-display text-2xl mt-1 tabular-nums" data-testid={`hero-slide-cta-clicks-${index}`}>{slide.analytics?.cta_clicks ?? 0}</div>
              </div>
              <div className="border border-border p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">CTR</div>
                <div className="font-display text-2xl mt-1 tabular-nums" data-testid={`hero-slide-ctr-${index}`}>
                  {(() => {
                    const i = slide.analytics?.impressions || 0;
                    const c = slide.analytics?.cta_clicks || 0;
                    if (!i) return "—";
                    return `${((c / i) * 100).toFixed(1)}%`;
                  })()}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Counters refresh on save/reload. Impressions fire once per browser session per slide.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroSlidesEditor;
